import { bool, func, object } from 'prop-types';
import React, { Component, createRef } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import styled, { withTheme } from 'styled-components';

import { DURATION } from 'ravenjs/constants/transitions';
import Menu from 'ravenjs/lib/Menu';
import PopoverContent from 'ravenjs/lib/PopoverContent';
import { getThemeProps } from 'ravenjs/utils/theme';
import { get } from 'ravenjs/utils/lodash';

import { ProfileIcon } from 'components/Icons';
import UserMenu from 'components/UserMenu';
import withPublicContent from 'components/PublicContent/PublicContentHOC';
import { EVENTS } from 'constants/events';
import { APP_PERMISSIONS } from 'constants/permissions';
import Acl from 'modules/acl';
import { actions as pendoActions } from 'modules/pendo';
import { selectors as userSelectors } from 'modules/user';
import { isNavigationV2Enabled as isNavV2Enabled } from 'utils/company';
import {
    DESKTOP_BREAKPOINT,
    DROPDOWN_OPEN_EVENT,
    HOVER_INTENT_DELAY,
    KEYBOARD_KEYS,
    SUBMENU_CLOSE_DELAY,
} from 'constants/navigation';

import { MIGRATED_COLORS } from 'constants/quartz/migrated_colors';
import UserNavStyled from './UserNavStyled';

const PopoverStylesV1 = styled(PopoverContent)`
    bottom: 0;
    margin-left: 8px;
    &:before {
        background: ${MIGRATED_COLORS.white};
        box-shadow: none;
        border-top: 1px solid ${MIGRATED_COLORS.black};
        border-right: 1px solid ${MIGRATED_COLORS.black};
    }
    max-width: 745px;
`;

const AvatarDropdownV2 = styled.div`
    position: absolute;
    top: 100%;
    right: 0;
    z-index: 1300;
    width: fit-content;
    max-width: min(1350px, calc(100vw - 5.125rem));
    border-radius: 4px 4px 8px 8px;
`;

const IconWrapper = styled.div`
    align-items: center;
    display: flex;
    position: relative;
    cursor: pointer;
    height: 100%;

    ${({ theme }) => theme.media.up('md')`
        margin: 0 .5625rem;
    `};
    ${({ theme }) => theme.media.down('md')`
        margin: 0;
    `};
`;

const UserNavWrapperV2 = styled.div`
    height: 100%;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: -12px;
    padding: 12px;
    box-sizing: content-box;
    position: relative;
    &:focus {
        outline: none;
    }
`;

// V2 icon wrapper for centering
const IconWrapperV2 = styled.div`
    align-items: center;
    display: flex;
    justify-content: center;
`;

class UserNav extends Component {
    static propTypes = {
        history: object,
        items: object,
        user: object,
        theme: object.isRequired,
        isSoftLogin: bool.isRequired,
        onNavClick: func,
        openLoginPrompt: func.isRequired,
        sendPendoEvent: func.isRequired,
        userCompany: object,
    };

    static defaultProps = {
        history: {},
        items: {},
        onNavClick: () => {},
        user: {},
        userCompany: {},
    };

    userNavRef = createRef();

    containerRef = createRef();

    menuContentRefV2 = createRef();

    constructor(props) {
        super(props);
        this.state = {
            hover: false,
            isDesktop: this.checkIsDesktop(),
            // Track focused menu item index for keyboard navigation
            focusedMenuItemIndex: -1,
        };

        this.hoverIntentTimeout = null;
        this.closeDelayTimeout = null;
        this._keyboardTriggered = false;
    }

    componentDidMount() {
        globalThis.addEventListener('resize', this.handleResize);
        document.addEventListener('mousedown', this.handleClickOutside);
        globalThis.addEventListener(DROPDOWN_OPEN_EVENT, this.handleOtherDropdownOpen);
    }

    componentWillUnmount() {
        globalThis.removeEventListener('resize', this.handleResize);
        document.removeEventListener('mousedown', this.handleClickOutside);
        globalThis.removeEventListener(DROPDOWN_OPEN_EVENT, this.handleOtherDropdownOpen);
        this.clearAllTimeouts();
    }

    isNavigationV2Enabled = () => {
        return isNavV2Enabled();
    };

    clearAllTimeouts = () => {
        if (this.hoverIntentTimeout) {
            clearTimeout(this.hoverIntentTimeout);
            this.hoverIntentTimeout = null;
        }
        if (this.closeDelayTimeout) {
            clearTimeout(this.closeDelayTimeout);
            this.closeDelayTimeout = null;
        }
    };

    checkIsDesktop = () => {
        return typeof globalThis !== 'undefined' && globalThis.innerWidth >= DESKTOP_BREAKPOINT;
    };

    handleResize = () => {
        const isDesktop = this.checkIsDesktop();
        if (isDesktop !== this.state.isDesktop) {
            this.setState(prevState => {
                const nextState = { isDesktop };
                // Close the menu when switching between desktop and mobile (V2 only)
                if (!isDesktop && this.isNavigationV2Enabled()) {
                    this.clearAllTimeouts();
                    if (prevState.hover) {
                        nextState.hover = false;
                    }
                }
                return nextState;
            });
        }
    };

    handleClickOutside = event => {
        if (!this.isNavigationV2Enabled()) {
            return;
        }

        // Check if the click is within the container (UserNavStyled)
        const isInContainer =
            this.containerRef.current && this.containerRef.current.contains(event.target);

        const menuContentRef = this.isNavigationV2Enabled()
            ? this.menuContentRefV2.current
            : this.userNavRef.current?.menuContentRef?.current;
        const isInMenuContent = menuContentRef && menuContentRef.contains(event.target);

        // Only close if the click is outside both the container and menu content
        if (!isInContainer && !isInMenuContent) {
            this.clearAllTimeouts();
            this.setState({ hover: false });
        }
    };

    handleOtherDropdownOpen = event => {
        if (!this.isNavigationV2Enabled()) {
            return;
        }
        // If another dropdown opened (not this one), close immediately
        if (event.detail && event.detail.source !== 'avatar') {
            this.clearAllTimeouts();
            this.setState({ hover: false });
        }
    };

    notifyDropdownOpen = () => {
        globalThis.dispatchEvent(
            new CustomEvent(DROPDOWN_OPEN_EVENT, {
                detail: { source: 'avatar' },
            })
        );
    };

    closeMenu = navLink => {
        const { onNavClick } = this.props;

        this.clearAllTimeouts();

        if (!this.isNavigationV2Enabled()) {
            const { current } = this.userNavRef;
            current && current.closeMenu();
        }

        this.setState({ hover: false });

        onNavClick(navLink);
    };

    renderMenuContent = () => {
        const { history, user } = this.props;
        let { items } = this.props;

        // TODO:  remove once BE change has been made to no longer provide this menu optoin
        const internalTools = get(items, 'internalTools', []).filter(
            item => item.state !== 'ticketingsystem'
        );
        items = { ...items, internalTools };

        return (
            <UserMenu
                history={history}
                user={user}
                items={items}
                onNavClick={navLink => {
                    this.closeMenu(navLink);
                }}
            />
        );
    };

    // Send Pendo event when avatar menu is opened
    sendAvatarMenuOpenedEvent = () => {
        const { sendPendoEvent, userCompany } = this.props;
        const { isDesktop } = this.state;
        const isNavV2Enabled = this.isNavigationV2Enabled();

        sendPendoEvent({
            ...EVENTS.NAVIGATION.AVATAR_MENU_OPENED,
            eventProperties: {
                ...EVENTS.NAVIGATION.AVATAR_MENU_OPENED.eventProperties,
                company_id: userCompany?.companyId,
                is_mobile: !isDesktop,
                nav_version: isNavV2Enabled ? 'V2' : 'V1',
            },
        });
    };

    onAvatarHover = () => {
        const { isSoftLogin } = this.props;
        if (isSoftLogin) {
            return;
        }

        const isNavV2Enabled = this.isNavigationV2Enabled();
        const { isDesktop } = this.state;

        // V2 behavior with hover-intent and persistent hover
        if (isNavV2Enabled) {
            // Clear any pending close timeout when entering
            if (this.closeDelayTimeout) {
                clearTimeout(this.closeDelayTimeout);
                this.closeDelayTimeout = null;
            }

            // For desktop V2, use hover-intent delay
            if (isDesktop) {
                if (this.hoverIntentTimeout) {
                    clearTimeout(this.hoverIntentTimeout);
                }

                this.hoverIntentTimeout = setTimeout(() => {
                    // Notify other dropdowns to close when this one opens
                    this.notifyDropdownOpen();
                    // Send Pendo event for avatar menu open tracking
                    this.sendAvatarMenuOpenedEvent();
                    this.setState({ hover: true });
                }, HOVER_INTENT_DELAY);
            } else {
                this.notifyDropdownOpen();
                // Send Pendo event for avatar menu open tracking
                this.sendAvatarMenuOpenedEvent();
                this.setState({ hover: true });
            }
        } else {
            // ORIGINAL V1 behavior - unchanged
            // Send Pendo event for avatar menu open tracking
            this.sendAvatarMenuOpenedEvent();
            this.setState({ hover: true });
        }
    };

    onAvatarClick = event => {
        const { isSoftLogin, openLoginPrompt } = this.props;
        if (isSoftLogin) {
            openLoginPrompt();
            return;
        }

        if (this.isNavigationV2Enabled() && this.state.isDesktop) {
            // If the click originated from inside the dropdown menu, let the menu item's
            // own onClick handle it — don't re-trigger focus to the first item
            const menuContentRef = this.menuContentRefV2.current;
            if (menuContentRef && menuContentRef.contains(event.target)) {
                this._keyboardTriggered = false;
                return;
            }

            if (this._keyboardTriggered) {
                this._keyboardTriggered = false;
                return;
            }
            this.clearAllTimeouts();
            this.notifyDropdownOpen();
            this.sendAvatarMenuOpenedEvent();
            this.setState({ hover: true, focusedMenuItemIndex: 0 }, () => {
                this.focusFirstMenuItemWithRetry(0);
            });
            return;
        }

        this.clearAllTimeouts();
        if (this.isNavigationV2Enabled()) {
            this.notifyDropdownOpen();
        }
        this.sendAvatarMenuOpenedEvent();
        this.setState({
            hover: true,
        });
    };

    onAvatarLeave = () => {
        const { isSoftLogin } = this.props;
        if (isSoftLogin) {
            return;
        }

        const isNavV2Enabled = this.isNavigationV2Enabled();
        const { isDesktop } = this.state;

        // V2 behavior with delay tolerance for persistent hover
        if (isNavV2Enabled) {
            // Clear hover intent if the user moves away before intent is confirmed
            if (this.hoverIntentTimeout) {
                clearTimeout(this.hoverIntentTimeout);
                this.hoverIntentTimeout = null;
            }

            // For desktop V2, use delay tolerance before closing
            if (isDesktop) {
                if (this.closeDelayTimeout) {
                    clearTimeout(this.closeDelayTimeout);
                }

                this.closeDelayTimeout = setTimeout(() => {
                    this.setState({ hover: false });
                }, SUBMENU_CLOSE_DELAY);
            } else {
                this.setState({ hover: false });
            }
        } else {
            // ORIGINAL V1 behavior - unchanged
            this.setState({ hover: false });
        }
    };

    // Cancel close when re-entering the menu area (V2 only)
    cancelClose = () => {
        if (this.closeDelayTimeout) {
            clearTimeout(this.closeDelayTimeout);
            this.closeDelayTimeout = null;
        }
    };

    // Handle keyboard navigation for the avatar dropdown
    handleKeyDown = event => {
        if (!this.isNavigationV2Enabled() || !this.state.isDesktop) {
            return;
        }

        const { hover } = this.state;

        switch (event.key) {
            case KEYBOARD_KEYS.ESCAPE:
                if (hover) {
                    event.preventDefault();
                    event.stopPropagation();
                    this.handleEscapeKey();
                }
                break;

            case KEYBOARD_KEYS.ENTER:
            case KEYBOARD_KEYS.SPACE:
                event.preventDefault();
                event.stopPropagation();
                this.handleEnterOrSpaceKey();
                break;

            case KEYBOARD_KEYS.ARROW_DOWN:
                if (hover) {
                    event.preventDefault();
                    event.stopPropagation();
                    this.handleArrowDownKey();
                }
                break;

            case KEYBOARD_KEYS.ARROW_UP:
                if (hover) {
                    event.preventDefault();
                    event.stopPropagation();
                    this.handleArrowUpKey();
                }
                break;

            case KEYBOARD_KEYS.TAB:
                // WCAG: Allow natural Tab/Shift+Tab behavior to move focus out of the menu
                // When the dropdown is open, close it but do NOT prevent default focus navigation
                if (hover) {
                    this.handleEscapeKey();
                }
                break;
            default:
                break;
        }
    };

    // Handle Escape key - close dropdown and return focus to avatar
    handleEscapeKey = () => {
        this.clearAllTimeouts();
        this.setState({ hover: false, focusedMenuItemIndex: -1 });

        // Return focus to the avatar container (UserNavStyled has tabIndex)
        if (this.containerRef.current) {
            this.containerRef.current.focus();
        }
    };

    handleEnterOrSpaceKey = () => {
        const { hover, focusedMenuItemIndex } = this.state;

        this._keyboardTriggered = true;

        if (!hover) {
            this.clearAllTimeouts();
            this.notifyDropdownOpen();
            // Send Pendo event for avatar menu open tracking (keyboard interaction)
            this.sendAvatarMenuOpenedEvent();
            this.setState(
                {
                    hover: true,
                    focusedMenuItemIndex: 0,
                },
                () => {
                    this.focusFirstMenuItemWithRetry(0);
                }
            );
        } else if (focusedMenuItemIndex >= 0) {
            // Dropdown is open, and an item is focused - activate it
            const menuItems = this.getMenuItems();
            if (menuItems && menuItems[focusedMenuItemIndex]) {
                menuItems[focusedMenuItemIndex].click();
            }
        }
    };

    // Handle Arrow Down key - navigate down within the menu
    handleArrowDownKey = () => {
        const { focusedMenuItemIndex } = this.state;
        const menuItems = this.getMenuItems();

        if (!menuItems || menuItems.length === 0) {
            return;
        }

        const nextIndex =
            focusedMenuItemIndex < menuItems.length - 1 ? focusedMenuItemIndex + 1 : 0; // Wrap to first item

        this.setState({ focusedMenuItemIndex: nextIndex }, () => {
            this.focusMenuItem(nextIndex);
        });
    };

    // Handle Arrow Up key - navigate up within the menu
    handleArrowUpKey = () => {
        const { focusedMenuItemIndex } = this.state;
        const menuItems = this.getMenuItems();

        if (!menuItems || menuItems.length === 0) {
            return;
        }

        const prevIndex =
            focusedMenuItemIndex > 0 ? focusedMenuItemIndex - 1 : menuItems.length - 1; // Wrap to last item

        this.setState({ focusedMenuItemIndex: prevIndex }, () => {
            this.focusMenuItem(prevIndex);
        });
    };

    getMenuContentEl = () => {
        if (this.isNavigationV2Enabled()) {
            return this.menuContentRefV2.current;
        }
        return this.userNavRef.current?.menuContentRef?.current;
    };

    getMenuItems = () => {
        const menuContentRef = this.getMenuContentEl();
        if (menuContentRef) {
            const menuItems = menuContentRef.querySelectorAll('[data-avatar-menu-item="true"]');
            return Array.from(menuItems);
        }
        return [];
    };

    focusFirstMenuItemWithRetry = (attempt = 0) => {
        setTimeout(() => {
            const menuContentEl = this.getMenuContentEl();
            const menuItems = this.getMenuItems();
            if (menuContentEl && menuItems && menuItems.length > 0) {
                menuContentEl.focus();
                requestAnimationFrame(() => {
                    menuItems[0].focus();
                });
                return;
            }
            if (attempt < 3) {
                this.focusFirstMenuItemWithRetry(attempt + 1);
            }
        }, 100);
    };

    // Focus a specific menu item by index
    focusMenuItem = index => {
        const menuItems = this.getMenuItems();
        if (menuItems && menuItems[index]) {
            menuItems[index].focus();
        }
    };

    renderButtonComponent = () => {
        const { theme } = this.props;
        const isNavigationV2Enabled = this.isNavigationV2Enabled();
        const IconWrapperComponent = isNavigationV2Enabled ? IconWrapperV2 : IconWrapper;
        const iconProps = isNavigationV2Enabled ? { width: '20', height: '20' } : {};
        const profileIconFillColor = isNavigationV2Enabled
            ? MIGRATED_COLORS.black
            : getThemeProps('NavItem.styles.color', MIGRATED_COLORS.black, { theme });
        return (
            <IconWrapperComponent>
                <ProfileIcon fill={profileIconFillColor} {...iconProps} />
            </IconWrapperComponent>
        );
    };

    renderV1MenuContent = () => {
        const { hover } = this.state;
        return (
            <Menu
                ButtonComponent={this.renderButtonComponent()}
                ContentComponent={PopoverStylesV1}
                ContentProps={{
                    borderRadius: '0',
                    elevation: 9,
                }}
                IconComponent={null}
                arrowSize={7}
                color="transparent"
                distanceFromContainer={0}
                placement="bottom-end"
                ref={this.userNavRef}
                open={hover}
                timeout={DURATION.none}
                borderRadius={4}
            >
                {this.renderMenuContent()}
            </Menu>
        );
    };

    renderV2MenuContent = () => {
        const { hover, focusedMenuItemIndex } = this.state;
        return (
            <>
                {this.renderButtonComponent()}
                {hover && (
                    <AvatarDropdownV2
                        ref={this.menuContentRefV2}
                        role="menu"
                        aria-label="User menu options"
                        id="avatar-menu-popover"
                        tabIndex={-1}
                        aria-activedescendant={
                            focusedMenuItemIndex >= 0
                                ? `avatar-menu-item-${focusedMenuItemIndex}`
                                : undefined
                        }
                        onMouseEnter={this.cancelClose}
                        onMouseLeave={this.onAvatarLeave}
                    >
                        {this.renderMenuContent()}
                    </AvatarDropdownV2>
                )}
            </>
        );
    };

    render() {
        const isNavigationV2Enabled = isNavV2Enabled();

        return (
            <UserNavStyled
                ref={this.containerRef}
                className={
                    Acl.check(APP_PERMISSIONS.userAssistanceClientAdmin)
                        ? 'pendo-nav-userprofile'
                        : ''
                }
                data-testid="user-avatar-menu-button"
                onFocus={isNavigationV2Enabled ? undefined : this.onAvatarHover}
                onMouseEnter={this.onAvatarHover}
                onClick={this.onAvatarClick}
                onMouseLeave={this.onAvatarLeave}
                onBlur={isNavigationV2Enabled ? undefined : this.onAvatarLeave}
                tabIndex={isNavigationV2Enabled ? 0 : undefined}
                onKeyDown={isNavigationV2Enabled ? this.handleKeyDown : undefined}
                role={isNavigationV2Enabled ? 'button' : undefined}
                aria-label={isNavigationV2Enabled ? 'User menu' : undefined}
                aria-haspopup={isNavigationV2Enabled ? 'menu' : undefined}
                aria-expanded={isNavigationV2Enabled ? this.state.hover : undefined}
                aria-controls={
                    isNavigationV2Enabled && this.state.hover ? 'avatar-menu-popover' : undefined
                }
            >
                {isNavigationV2Enabled ? (
                    <UserNavWrapperV2>{this.renderV2MenuContent()}</UserNavWrapperV2>
                ) : (
                    this.renderV1MenuContent()
                )}
            </UserNavStyled>
        );
    }
}

const mapStateToProps = createStructuredSelector({
    userCompany: userSelectors.getUserCompany,
});

const mapDispatchToProps = {
    sendPendoEvent: pendoActions.sendPendoEvent,
};

export { UserNav as UserNavUnwrapped };
export default withPublicContent(withTheme(connect(mapStateToProps, mapDispatchToProps)(UserNav)));
